import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { FlatButton, GhostButton, TransparentFlatButton } from '@ui/buttons';
import { CONTROL_HEIGHT } from '@features/scoreSpec/SegmentedControl';
import React from 'react';

// Shared styled-components for the fmdc feature: the CSS-grid table every list here is drawn
// with, the filter-bar controls that line up with a SegmentedControl, and the two card tones.
// Copied from BackfillHistory / SpecList rather than imported, because those files do not export
// them; the token split is the house one — an INTERACTIVE surface uses `border`, a PASSIVE panel
// `lowContrastBorder`.

// ---- table ---------------------------------------------------------------------------------------

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.div`
  min-width: ${({ $minWidth }) => $minWidth || 900}px;
`;

export const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 16px;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};
`;

// `$interactive` rows take the pointer and the focus ring; a row that is not a link (an audit
// event, a static-info field) leaves both off rather than promising a click that does nothing.
export const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns};
  gap: 16px;
  align-items: center;
  padding: 12px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  background: ${({ theme, $selected }) => ($selected ? theme.themeColors.hoverComponentBackground : 'transparent')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};

  ${({ $interactive, $selected, theme }) =>
    $interactive
      ? `
    &:hover {
      background: ${$selected ? theme.themeColors.hoverComponentBackground : theme.themeColors.componentNoOpacity};
    }

    &:focus-visible {
      outline: 2px solid ${theme.themeColors.primaryCtaButton};
      outline-offset: -2px;
    }
  `
      : ''}
`;

// Enter / Space open a focusable row, the BackfillHistory handler. Space is prevented so it does
// not scroll the page.
export const rowKeyHandler = (onOpen) => (e) => {
  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
    if (e.key !== 'Enter') e.preventDefault();
    onOpen();
  }
};

// Bold, full text — not `isLowContrast` as BackfillHistory's headers are. A header is what makes a
// table scannable, and at 12px the dim tone was the first thing readers said they could not read.
export const HeaderCell = ({ children }) => <Body7 bold>{children}</Body7>;

// ---- filter bar ----------------------------------------------------------------------------------

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px 24px;
  align-items: flex-end;
`;

export const SearchInput = styled.input`
  height: ${CONTROL_HEIGHT};
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 13px;
  min-width: 220px;

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

// A bare styled select rather than @ui/inputs' Select: that one is a full-width labelled form field
// (38px, its own <label>, a hint slot) and would claim a whole line of the filter bar. Same rule as
// SpecList's FilterSelect.
export const FilterSelect = styled.select`
  height: ${CONTROL_HEIGHT};
  width: ${({ $width }) => $width || 190}px;
  padding: 0 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }

  option {
    color: ${({ theme }) => theme.themeColors.text};
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

// ---- cards and sections --------------------------------------------------------------------------

export const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  padding: 16px;
`;

export const PassiveCard = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  padding: 16px;
`;

// A bordered box in the error colour, for a run's error text or a failed setup (BackfillProgress's
// ErrorBox).
export const ErrorBox = styled.div`
  border: 1px solid ${({ theme }) => theme.themeColors.negative};
  border-radius: 6px;
  padding: 16px;
`;

// Count cards in a row (the run overview's model cells / rows / proposals).
export const CardRow = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
`;

export const SectionHead = ({ heading, subtext }) => (
  <>
    <Body5 bold>{heading}</Body5>
    {subtext && (
      <Container top={2}>
        <Body7 isLowContrast>{subtext}</Body7>
      </Container>
    )}
  </>
);

// Monospace inline text for codes, cells and keys.
export const Mono = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  color: ${({ theme }) => theme.themeColors.text};
  word-break: break-all;
`;

// Error-tone wrapper around text components (BackfillProgress's ErrorText).
export const ErrorTone = styled.div`
  color: ${({ theme }) => theme.themeColors.error};

  .base-text {
    color: ${({ theme }) => theme.themeColors.error};
  }
`;

// The danger treatment for a destructive button (SpecLifecycleBar's DangerFlatButton): the error
// colour as the fill, so Reject / Dismiss / Retire read as what they are.
const DangerWrap = styled.div`
  button {
    background: ${({ theme }) => theme.themeColors.error};
    border-color: ${({ theme }) => theme.themeColors.error};
  }
`;

export const DangerFlatButton = ({ children, ...rest }) => (
  <DangerWrap>
    <FlatButton {...rest}>{children}</FlatButton>
  </DangerWrap>
);

const DangerTextWrap = styled.div`
  button,
  button * {
    color: ${({ theme }) => theme.themeColors.error};
  }
`;

export const DangerTransparentButton = ({ children, ...rest }) => (
  <DangerTextWrap>
    <TransparentFlatButton {...rest}>{children}</TransparentFlatButton>
  </DangerTextWrap>
);

// The one underlined text link for an inline action that must not read as a CTA: Open, Dismiss, a
// run id in a cell, a "show more". Six files used to carry their own copy of this in two colours,
// and none of them had a disabled state, so a link whose action was in flight still looked
// pressable. One declaration, `text` colour, and a disabled rule.
export const LinkButton = styled(GhostButton)`
  font-size: 12px;
  text-decoration: underline;
  color: ${({ theme }) => theme.themeColors.text};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

// A kept file, as a chip: name plus a round remove control. Shared by the model upload and the
// settings template upload so the same object is drawn one way. @ui/File is not used because it
// hard-codes a .csv suffix on the name.
export const FileChip = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 12px;
  border-radius: 5px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  max-width: 100%;

  svg {
    height: 14px;
    width: 14px;
    flex-shrink: 0;
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

export const FileChipRemoveButton = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 16px;
  color: ${({ theme }) => theme.themeColors.text};

  &:hover {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  }
`;

// Two columns at 1024px and above, stacked below (the value detail's details + source grid).
export const TwoColumn = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
`;
